import type { ViewMode } from './ProductsContext'

export type CustomViewDataPoint
  // Main information
  = | 'productPicture'
    | 'dmiSegmentation'
    | 'wholesaleSegmentation'
    | 'price'
    | 'customerReview'
  // Product information
    | 'brand'
    | 'modelVariations'
    | 'sizeGrid'
    | 'gender'
    | 'productNature'
    | 'composition'
    | 'ecodesignAndCO2'
    | 'productBenefits'
    | 'internetTeaser'
    | 'designedIn'
    | 'distributionCustomsZones'
    | 'producedIn'
    | 'homologatedIn'
  // Product status
    | 'availabilityDates'
    | 'validationStatus'
  // Business data
    | 'gmv'
    | 'salesQuantity'
    | 'margin'
    | 'costPlus'
    | 'markup'
    | 'gmvCO2'
  // Supply data
    | 'moq'

export interface CustomViewSelections {
  selectedItems: CustomViewDataPoint[]
}

export type CustomViewTarget = 'gallery' | 'list'

export const ALL_SELECTABLE_DATA_POINTS: CustomViewDataPoint[] = [
  'productPicture',
  'dmiSegmentation',
  // 'wholesaleSegmentation',
  'price',
  'customerReview',
  'modelVariations',
  'brand',
  'sizeGrid',
  'gender',
  'productNature',
  'composition',
  'ecodesignAndCO2',
  'productBenefits',
  'internetTeaser',
  'designedIn',
  'distributionCustomsZones',
  // 'producedIn',
  'homologatedIn',
  'availabilityDates',
  'validationStatus',
  // 'gmv',
  // 'salesQuantity',
  // 'margin',
  // 'costPlus',
  // 'markup',
  // 'gmvCO2',
  // 'moq',
]

export const LIST_SELECTABLE_DATA_POINTS: CustomViewDataPoint[] = [
  'dmiSegmentation',
  'price',
  'customerReview',
  'brand',
  'gender',
  'productNature',
  'composition',
  'productBenefits',
  'internetTeaser',
  'designedIn',
  'distributionCustomsZones',
  'availabilityDates',
]

/**
 * Fields that are only available for models (locked off in SM view).
 * Tooltip: "Only displayed for models"
 */
export const SM_VIEW_LOCKED_OFF: CustomViewDataPoint[] = [
  'ecodesignAndCO2',
  'homologatedIn',
  'validationStatus',
  'moq',
]

/**
 * Fields that are only available for supermodels (locked off in model view).
 * Tooltip: "Only displayed for supermodels"
 */
export const MODEL_VIEW_LOCKED_OFF: CustomViewDataPoint[] = [
  'modelVariations',
]

export function getLockedOff(viewMode: ViewMode): CustomViewDataPoint[] {
  return viewMode === 'sm' ? SM_VIEW_LOCKED_OFF : MODEL_VIEW_LOCKED_OFF
}

export function getSelectableItems(viewMode: ViewMode): CustomViewDataPoint[] {
  return ALL_SELECTABLE_DATA_POINTS.filter(
    item => !getLockedOff(viewMode).includes(item),
  )
}

export const DEFAULT_SELECTIONS: CustomViewSelections = {
  selectedItems: ['productPicture', 'price', 'customerReview', 'distributionCustomsZones'],
}

export const DEFAULT_LIST_SELECTIONS: CustomViewSelections = {
  selectedItems: ['price', 'customerReview', 'gender', 'productBenefits', 'designedIn', 'distributionCustomsZones'],
}

const STORAGE_KEYS: Record<CustomViewTarget, string> = {
  gallery: 'offer-catalog-custom-view',
  list: 'offer-catalog-list-custom-view',
}

export function loadSelectionsFromStorage(target: CustomViewTarget = 'gallery'): CustomViewSelections | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS[target])
    if (!stored)
      return null
    return JSON.parse(stored) as CustomViewSelections
  }
  catch {
    return null
  }
}

export function saveSelectionsToStorage(selections: CustomViewSelections, target: CustomViewTarget = 'gallery'): void {
  try {
    globalThis.localStorage.setItem(STORAGE_KEYS[target], JSON.stringify(selections))
  }
  catch {
    // ignore storage errors
  }
}
